import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { MultiselectOption } from '@cbo/shared-library';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import { useFormContext } from 'react-hook-form';
import { TextFieldInputAdornment } from '../SingleSelect/SingleSelectTextField';

export interface MultiselectTextFieldProps {
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLInputElement | HTMLButtonElement | null>>;
  anchorEl: HTMLInputElement | HTMLButtonElement | null;
  optionsLength: number;
  pluralName: string;
  controllerName: string;
  selected: MultiselectOption[];
  handleReset: (isFullRest?: boolean) => void;
  resetEnabled: boolean;
  textFieldLabel?: string;
  isRequired?: boolean;
}

const getSelectedString = (t: TFunction, selected: MultiselectOption[], pluralName: string, optionsLength: number) => {
  if (selected.length === 0) return '';
  const allText = t(`scheduleReports.reportSettingsForm.all`);
  const allSelectedText = `${allText} ${pluralName}`;
  if (selected.length === optionsLength) return allSelectedText;

  return selected.length === 1 ? `${selected[0].label}` : `${selected.length} ${pluralName}`;
};

function MultiselectTextField({
  setAnchorEl,
  anchorEl,
  controllerName,
  optionsLength,
  pluralName,
  selected,
  handleReset,
  resetEnabled,
  textFieldLabel,
  isRequired,
}: Readonly<MultiselectTextFieldProps>) {
  const { t } = useTranslation();
  const {
    formState: { errors },
  } = useFormContext();
  const error = errors[controllerName];
  const errorMessage = typeof error?.message === 'string' ? error.message : '';

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const selectedString = getSelectedString(t, selected, pluralName, optionsLength);
  return (
    <TextField
      fullWidth
      label={textFieldLabel}
      data-testid={`${controllerName}-multiselect-text-field`}
      variant='standard'
      onClick={handleClick}
      required={isRequired}
      InputProps={{
        readOnly: true,
        startAdornment: (
          <InputAdornment position='start'>
            {selected.length > 0 && <Chip label={selectedString} size='small' />}
          </InputAdornment>
        ),
        endAdornment: (
          <TextFieldInputAdornment
            textFieldType='multiselect'
            resetEnabled={resetEnabled}
            controllerName={controllerName}
            handleReset={handleReset}
            open={open}
          />
        ),
      }}
      error={!!error}
      helperText={errorMessage}
    />
  );
}

MultiselectTextField.defaultProps = {
  textFieldLabel: '',
  isRequired: false,
};

export default MultiselectTextField;
